// The filters a visitor has applied, as chips above the results: one chip per
// choice, each with its own ×, so it is plain what narrows the list and how to
// undo any one part of it (listing browse plan, 2026-09-23).

import { formatPrice } from '@/lib/format';
import { propertyTypeLabel } from '@/lib/property-types';
import { provinceName } from '@/lib/provinces';
import { AMENITIES, BASEMENT_STATUSES, HEATING_TYPES, TAGS, optionLabel } from '@/lib/filter-options';
import { numberFormat } from '@/lib/format';
import { availableFromLabel, furnishedLabel, isFurnished, isRentIncluded, rentIncludedLabel } from '@/lib/rental-terms';

export type FilterChip = {
  /** The query parameter the chip stands for. */
  key: string;
  /** For a list parameter (propertyType, amenities), the one value it removes. */
  value?: string;
  /** Other parameters that go with it (a tag sets a type or a price too). */
  alsoClears?: string[];
  label: string;
};

type Params = URLSearchParams | Record<string, unknown>;

function read(params: Params, key: string): string {
  if (params instanceof URLSearchParams) return params.get(key) || '';
  const value = params[key];
  return Array.isArray(value) ? value.join(',') : value == null ? '' : String(value);
}

const set = (v: string) => v !== '' && v !== 'any' && v !== 'all';

function area(value: string, fr: boolean): string {
  const n = numberFormat(fr ? 'fr-CA' : 'en-CA', { maximumFractionDigits: 0 }).format(Number(value));
  return fr ? `${n} pi²` : `${n} sq ft`;
}

/**
 * Chips for what narrows the results, in the order the filter panel lists
 * them. Paging, sorting and the favourites view are not filters.
 */
export function filterChips(params: Params, lang: string): FilterChip[] {
  const fr = lang === 'fr';
  const chips: FilterChip[] = [];
  const v = (key: string) => read(params, key);

  if (set(v('q'))) chips.push({ key: 'q', label: fr ? `« ${v('q')} »` : `“${v('q')}”` });
  if (set(v('city'))) chips.push({ key: 'city', alsoClears: ['province'], label: v('city') });
  else if (set(v('province'))) chips.push({ key: 'province', label: provinceName(v('province'), lang) });

  // A tag sets the matching filter as well; removing the chip removes both.
  const tag = v('tag');
  if (set(tag)) {
    const alsoClears =
      tag === 'house' || tag === 'condo' ? ['propertyType']
        : tag === 'luxury' ? ['minPrice']
          : tag === 'low_condo_fees' ? ['maxCondoFees']
            : tag === 'with_garage' ? ['garageSpaces']
              : [];
    chips.push({ key: 'tag', alsoClears, label: optionLabel(TAGS, tag, lang) });
  }
  const tagOwns = new Set(chips.find((c) => c.key === 'tag')?.alsoClears || []);

  if (v('ownerType') === 'private') chips.push({ key: 'ownerType', label: fr ? 'Particuliers' : 'Private sellers' });
  if (v('ownerType') === 'agency') chips.push({ key: 'ownerType', label: fr ? 'Agences' : 'Agencies' });

  if (!tagOwns.has('propertyType')) {
    for (const type of v('propertyType').split(',').filter(set)) {
      chips.push({ key: 'propertyType', value: type, label: propertyTypeLabel(type, lang) });
    }
  }
  if (set(v('minPrice')) && !tagOwns.has('minPrice')) {
    chips.push({ key: 'minPrice', label: `${fr ? 'À partir de' : 'From'} ${formatPrice(v('minPrice'), lang)}` });
  }
  if (set(v('maxPrice'))) chips.push({ key: 'maxPrice', label: `${fr ? 'Jusqu’à' : 'Up to'} ${formatPrice(v('maxPrice'), lang)}` });
  if (set(v('bedrooms'))) chips.push({ key: 'bedrooms', label: `${v('bedrooms')}+ ${fr ? 'chambres' : 'bedrooms'}` });
  if (set(v('bathrooms'))) chips.push({ key: 'bathrooms', label: `${v('bathrooms')}+ ${fr ? 'salles de bain' : 'bathrooms'}` });
  if (set(v('maxCondoFees')) && !tagOwns.has('maxCondoFees')) {
    chips.push({ key: 'maxCondoFees', label: `${fr ? 'Frais jusqu’à' : 'Condo fees up to'} ${formatPrice(v('maxCondoFees'), lang)}` });
  }
  if (set(v('heatingType'))) chips.push({ key: 'heatingType', label: optionLabel(HEATING_TYPES, v('heatingType'), lang) });
  if (set(v('basementStatus'))) {
    chips.push({ key: 'basementStatus', label: `${fr ? 'Sous-sol : ' : 'Basement: '}${optionLabel(BASEMENT_STATUSES, v('basementStatus'), lang)}` });
  }
  if (set(v('garageSpaces')) && Number(v('garageSpaces')) > 0 && !tagOwns.has('garageSpaces')) {
    chips.push({ key: 'garageSpaces', label: `Garage ${v('garageSpaces')}+` });
  }
  if (set(v('drivewaySpaces')) && Number(v('drivewaySpaces')) > 0) {
    chips.push({ key: 'drivewaySpaces', label: `${fr ? 'Stationnement' : 'Parking'} ${v('drivewaySpaces')}+` });
  }
  // The search takes 'yes' and 'no' (lib/property-search.ts).
  if (v('petFriendly') === 'yes') chips.push({ key: 'petFriendly', label: fr ? 'Animaux acceptés' : 'Pets allowed' });
  if (v('petFriendly') === 'no') chips.push({ key: 'petFriendly', label: fr ? 'Sans animaux' : 'No pets' });
  if (set(v('minArea'))) chips.push({ key: 'minArea', label: `${fr ? 'À partir de' : 'From'} ${area(v('minArea'), fr)}` });
  if (set(v('maxArea'))) chips.push({ key: 'maxArea', label: `${fr ? 'Jusqu’à' : 'Up to'} ${area(v('maxArea'), fr)}` });
  if (set(v('minYear'))) chips.push({ key: 'minYear', label: `${fr ? 'Construit dès' : 'Built from'} ${v('minYear')}` });
  if (set(v('maxYear'))) chips.push({ key: 'maxYear', label: `${fr ? 'Construit jusqu’en' : 'Built until'} ${v('maxYear')}` });
  // A rental's lease (rentals filters, 2026-09-24).
  if (set(v('availableBy'))) {
    const when = availableFromLabel(v('availableBy'), lang);
    if (when) chips.push({ key: 'availableBy', label: when === 'Now' || when === 'Maintenant' ? (fr ? 'Libre maintenant' : 'Free now') : fr ? `Libre d’ici le ${when}` : `Free by ${when}` });
  }
  for (const f of v('furnished').split(',').filter(isFurnished)) {
    chips.push({ key: 'furnished', value: f, label: furnishedLabel(f, lang)! });
  }
  for (const r of v('rentIncludes').split(',').filter(isRentIncluded)) {
    chips.push({ key: 'rentIncludes', value: r, label: fr ? `Inclus : ${rentIncludedLabel(r, lang)}` : `Includes: ${rentIncludedLabel(r, lang)}` });
  }
  for (const amenity of v('amenities').split(',').filter(set)) {
    chips.push({ key: 'amenities', value: amenity, label: optionLabel(AMENITIES, amenity, lang) });
  }
  return chips;
}

/** The query string without one chip, back on the first page. */
export function withoutChip(params: URLSearchParams, chip: FilterChip): URLSearchParams {
  const next = new URLSearchParams(params.toString());
  if (chip.value !== undefined) {
    const rest = (next.get(chip.key) || '').split(',').filter((x) => x && x !== chip.value);
    if (rest.length) next.set(chip.key, rest.join(','));
    else next.delete(chip.key);
  } else {
    next.delete(chip.key);
  }
  for (const key of chip.alsoClears || []) next.delete(key);
  next.delete('page');
  return next;
}

/** The query string with every filter removed: only the sort stays. */
export function withoutFilters(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  const sort = params.get('sort');
  if (sort) next.set('sort', sort);
  return next;
}
